import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  UploadSimple,
  FileCsv,
  Sparkle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/AppHeader";
import { api, formatApiErrorDetail } from "@/lib/api";

const COLUMNS = [
  "slab_color",
  "material",
  "finish",
  "thickness",
  "length_in",
  "width_in",
  "quantity",
  "supplier",
  "serial_number",
  "barcode",
  "lot_number",
  "location",
  "cost_per_sqft",
  "sale_price_per_sqft",
  "stone_group",
  "status",
  "notes",
  "price_contractor",
  "price_designer",
  "price_builder",
  "price_commercial",
  "price_regular",
];

export default function SlabImport() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("add");

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/slabs/import/preview", fd);
      setPreview(data);
      setFileName(file.name);
      toast.success(
        `${data.count} row${data.count === 1 ? "" : "s"} read — review below`,
      );
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not read that file",
      );
    }
    setBusy(false);
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/slabs/import", {
        rows: preview.rows,
        mode,
      });
      toast.success(
        mode === "update"
          ? `${data.updated} topped up · ${data.created} new slabs added`
          : `${data.created} slabs added to inventory`,
      );
      navigate("/inventory/slabs");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Import failed",
      );
    }
    setBusy(false);
  };

  const template = () => {
    const blob = new Blob(
      [
        `${COLUMNS.join(",")}\nCalacatta Gold,Quartz,Polished,3cm,126,63,1,Stone Source,SN-1001,,LOT-1,A-12,18.50,45,Group 7,In Stock,,52,56,58,60,45\n`,
      ],
      {
        type: "text/csv",
      },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "slab-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Settings / Import Slabs" />
      <main className="relative z-10 pb-28 pt-6">
        <Link
          to="/settings"
          data-testid="back-to-settings"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-white"
        >
          <ArrowLeft size={14} weight="bold" /> Settings
        </Link>
        <h1 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">
          Import your slabs
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          Upload a CSV of your slab inventory. Every row is shown for review
          before anything is saved.
        </p>

        <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-6">
          <div className="rounded-lg border border-dashed border-zinc-700 p-10 text-center">
            <UploadSimple
              size={30}
              weight="bold"
              className="mx-auto text-zinc-500"
            />
            <div className="mt-3 text-base font-semibold">
              Drop your slab list here
            </div>
            <div className="mt-1 text-xs text-zinc-500">
              CSV with a header row — up to 15MB
            </div>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              <Button
                data-testid="choose-slab-file"
                disabled={busy}
                onClick={() => inputRef.current?.click()}
                className="h-11 bg-indigo-600 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-500"
              >
                <FileCsv size={16} weight="bold" className="mr-2" />
                {busy ? "Reading…" : "Choose file"}
              </Button>
              <Button
                data-testid="download-template"
                variant="outline"
                onClick={template}
                className="h-11 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
              >
                Download template
              </Button>
            </div>
            <input
              ref={inputRef}
              data-testid="slab-import-input"
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={pick}
            />
          </div>
          <p className="mt-4 flex items-start gap-2 text-xs text-zinc-500">
            <Sparkle
              size={14}
              weight="bold"
              className="mt-0.5 shrink-0 text-indigo-300"
            />
            Column headers are matched by name, so extra columns are ignored.
            Only the slab color is required — every other column can be left
            blank and rows without a color are listed as skipped instead of
            being guessed.
          </p>
        </div>

        <div
          className="mt-4 flex flex-wrap gap-2"
          data-testid="import-mode-toggle"
        >
          {[
            ["add", "Add as new slabs"],
            ["update", "Top up existing colors"],
          ].map(([v, label]) => (
            <button
              key={v}
              data-testid={`import-mode-${v}`}
              onClick={() => setMode(v)}
              className={`rounded-full border px-4 py-2 text-[11px] uppercase tracking-widest transition-colors duration-150 ${
                mode === v
                  ? "border-indigo-400/50 bg-indigo-500/10 text-indigo-300"
                  : "border-zinc-800 bg-zinc-900 text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {label}
            </button>
          ))}
          <span className="self-center text-xs text-zinc-500">
            {mode === "update"
              ? "Rows matching an existing slab color add to its quantity; new colors are created."
              : "Every row becomes a new slab record."}
          </span>
        </div>

        {preview && (
          <div className="mt-6" data-testid="import-preview">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  {fileName}
                </div>
                <div className="text-sm text-zinc-300">
                  <span className="font-mono-data text-emerald-400">
                    {preview.count}
                  </span>{" "}
                  ready to import
                  {preview.errors.length > 0 && (
                    <>
                      {" · "}
                      <span className="font-mono-data text-red-400">
                        {preview.errors.length}
                      </span>{" "}
                      skipped
                    </>
                  )}
                </div>
              </div>
              <Button
                data-testid="confirm-import-button"
                disabled={busy || preview.count === 0}
                onClick={confirm}
                className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
              >
                {busy
                  ? "Importing…"
                  : mode === "update"
                    ? `Top Up From ${preview.count} Rows`
                    : `Import ${preview.count} Slabs`}
              </Button>
            </div>

            {preview.errors.length > 0 && (
              <div
                className="mt-3 rounded-lg border border-red-500/30 bg-red-500/5 p-4"
                data-testid="import-errors"
              >
                {preview.errors.slice(0, 8).map((e) => (
                  <div key={e} className="text-xs text-red-300">
                    {e}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-3 overflow-x-auto rounded-lg border border-zinc-800 bg-zinc-900">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500">
                    <th className="p-3">Row</th>
                    <th className="p-3">Color</th>
                    <th className="p-3">Spec</th>
                    <th className="p-3">Size</th>
                    <th className="p-3">Rack</th>
                    <th className="p-3 text-right">Cost</th>
                    <th className="p-3 text-right">Sale</th>
                    <th className="p-3">Group prices</th>
                    <th className="p-3">Group</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr
                      key={r._row}
                      data-testid={`import-row-${r._row}`}
                      className="border-b border-zinc-800 last:border-0"
                    >
                      <td className="font-mono-data p-3 text-xs text-zinc-500">
                        {r._row}
                      </td>
                      <td className="p-3 font-semibold">{r.slab_color}</td>
                      <td className="p-3 text-zinc-400">
                        {[r.material, r.finish, r.thickness]
                          .filter(Boolean)
                          .join(" · ")}
                      </td>
                      <td className="font-mono-data p-3 text-zinc-400">
                        {r.length_in}" x {r.width_in}"
                      </td>
                      <td className="p-3 text-zinc-400">{r.location || "—"}</td>
                      <td className="font-mono-data p-3 text-right text-zinc-400">
                        {r.cost_per_sqft != null ? `$${r.cost_per_sqft}` : "—"}
                      </td>
                      <td className="font-mono-data p-3 text-right text-yellow-400">
                        {r.sale_price_per_sqft != null
                          ? `$${r.sale_price_per_sqft}`
                          : "—"}
                      </td>
                      <td
                        className="font-mono-data p-3 text-xs text-emerald-300"
                        data-testid={`import-group-prices-${r._row}`}
                      >
                        {Object.keys(r.group_prices || {}).length
                          ? Object.entries(r.group_prices)
                              .map(([g, v]) => `${g.slice(0, 4)} $${v}`)
                              .join(" · ")
                          : "—"}
                      </td>
                      <td className="p-3 text-zinc-400">
                        {r.stone_group || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
